import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { useRef } from "react";

import { type GetPlacementApplicationsResponse } from "../types/fetchPlacementApplications.schema";
import {
  type FetchPlacementApplicationsParams,
  useFetchPlacementApplications,
} from "./useFetchPlacementApplications";

/**
 * Fetch placement applications that have upcoming interviews (start > now),
 * sorted by interview start date ascending (soonest first).
 *
 * Used by the "Interviews" section on the My Jobs tab.
 */
export function useGetUpcomingInterviewApplications(
  params: Pick<FetchPlacementApplicationsParams, "workerId" | "pageSize">,
  options: UseInfiniteQueryOptions<GetPlacementApplicationsResponse, AxiosError> = {}
) {
  // Capture mount time so the query key stays stable across renders.
  // Also exported as `cutoffDate` so the consumer can use the same
  // reference for client-side filtering, avoiding time drift.
  const cutoffDate = useRef(new Date()).current;
  const nowIso = useRef(cutoffDate.toISOString()).current;

  const query = useFetchPlacementApplications(
    {
      workerId: params.workerId,
      sort: "asc",
      pageSize: params.pageSize ?? 100,
      filter: {
        "interview.start": {
          gt: nowIso,
        },
      },
    },
    options
  );

  return { ...query, cutoffDate };
}
